import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { useOutletContext } from "react-router-dom"
import StateData from 'components/reporting/components/StateData/StateData'
import Bloc from 'components/reporting/components/Bloc/Bloc'
import DoughnutChart from 'components/reporting/components/graphs/Doughnut/Doughnut'
import HistogramChart from 'components/reporting/components/graphs/Histogram/Histogram'
import { formatNb } from 'functions'
import { getDatasFrom } from 'helpers/chartjs'
import colors from 'components/reporting/styles/colors.scss'

const Overview = () => {
  const { t } = useTranslation()
  const [contract] = useOutletContext()
  const [insuredProducts, setInsuredProducts] = useState(null)
  const [claimsOpened, setClaimsOpened] = useState(null)
  const [totalRevenue, setTotalRevenue] = useState(null)
  const [revenuePerProduct, setRevenuePerProduct] = useState(null)
  const [insurancesAndClaims, setInsurancesAndClaims] = useState(null)
  const [adhesionPerPackage, setAdhesionPerPackage] = useState(null)
  const [topDistributors, setTopDistributors] = useState(null)
  const [topClaims, setTopClaims] = useState(null)
  const [displayGraphs, setDisplayGraphs] = useState(false)
  const totalAdhesionPerPackage = formatNb(adhesionPerPackage?.data?.reduce( (acc, cur) => acc + cur ,0))
  const totalDistributors = formatNb(topDistributors?.data?.reduce( (acc, cur) => acc + cur ,0))
  

  useEffect(() => {
    if (!contract?._id) {
      setDisplayGraphs(false)
      return
    }

    const controller = new AbortController()
    const { signal } = controller

    // Reset datas
    setDisplayGraphs(true)
    setInsuredProducts(null)
    setTotalRevenue(null)
    setClaimsOpened(null)
    setRevenuePerProduct(null)
    setInsurancesAndClaims(null)
    setAdhesionPerPackage(null)
    setTopDistributors(null)
    setTopClaims(null)

    getDatasFrom({ contract, signal, endpoint: 'insuredProducts' }).then(setInsuredProducts)
    getDatasFrom({ contract, signal, endpoint: 'totalRevenue' }).then(setTotalRevenue)
    getDatasFrom({ contract, signal, endpoint: 'claimsOpened' }).then(setClaimsOpened)
    getDatasFrom({ contract, signal, endpoint: 'revenuePerProduct' }).then(setRevenuePerProduct)
    getDatasFrom({ contract, signal, endpoint: 'insuredAndClaims' }).then(setInsurancesAndClaims)
    getDatasFrom({ contract, signal, endpoint: 'adhesionPerPackage' }).then(setAdhesionPerPackage)
    getDatasFrom({ contract, signal, endpoint: 'topDistributors' }).then(setTopDistributors)
    getDatasFrom({ contract, signal, endpoint: 'topClaims' }).then(setTopClaims)

    return () => {
      controller.abort()
    }
  }, [contract])

  return displayGraphs 
  ? (
    <>
      <StateData
        title={t('reporting:graphs.insuredProducts')}
        data={insuredProducts}
        size={!contract?.partner ? 'mid' : undefined}
        footerTitle={t('reporting:graphs.pastMonth')} 
      />

      {contract?.partner && <StateData
        title={t('reporting:graphs.totalRevenue')}
        data={totalRevenue}
        footerTitle={t('reporting:graphs.pastMonth')}
      />}

      <StateData
        title={t('reporting:graphs.claimsOpened')}
        data={claimsOpened}
        size={!contract?.partner ? 'mid' : undefined}
        footerTitle={t('reporting:graphs.thisMonth')}
      />

      {contract?.partner && <Bloc
        title={t('reporting:graphs.revenuePerProduct.title')}
        subtitle={t('reporting:graphs.range', {type: '(Euros)'})}
        size='mid'
        content={<HistogramChart
          backgroundColor={[
            colors.blue1,
            colors.blue4,
            colors.orange2,
            colors.orange4
          ]}
          legendID='legendRevenuePerProduct'
          datas={revenuePerProduct}
          stacked={true}
          monthsAxisX={true}
          dataType={'€'}
        />}
      />}

      <Bloc
        title={t('reporting:graphs.insurancesAndClaims.title')}
        subtitle={t('reporting:graphs.range', {type: null})}
        size={contract?.partner && 'mid'}
        content={<HistogramChart
          backgroundColor={[
            colors.blue4,
            colors.blue1
          ]}
          legendID='legendInsurancesAndClaims'
          datas={insurancesAndClaims}
          monthsAxisX={true}
        />}
      />

      <Bloc
        title={t('reporting:graphs.adhesionPerPackage.title')}
        subtitle={t('reporting:graphs.adhesionPerPackage.subtitle', {number: totalAdhesionPerPackage})}
        size='tier'
        content={<DoughnutChart
          backgroundColor={[
            colors.blue1,
            colors.blue2,
            colors.blue3,
            colors.blue4,
            colors.blue5
          ]}
          legendID='legendRateType'
          datas={adhesionPerPackage}
        />}
      />

      <Bloc
        title={t('reporting:graphs.topDistributor.title')}
        subtitle={t('reporting:graphs.topDistributor.subtitle', {number: totalDistributors})}
        size='tier'
        content={<DoughnutChart
          backgroundColor={[
            colors.orange1,
            colors.orange2,
            colors.orange3,
            colors.orange4,
            colors.orange5
          ]}
          legendID='legendTopDistributor'
          datas={topDistributors}
        />}
      />

      <Bloc
        title={t('reporting:graphs.topClaims.title')}
        subtitle={t('reporting:graphs.topClaims.subtitle')}
        size='tier'
        content={<DoughnutChart
          backgroundColor={[
            colors.grey1,
            colors.grey2,
            colors.grey3,
            colors.grey4,
            colors.grey5
          ]}
          legendID='legendTopClaims'
          datas={topClaims}
        />}
      />
    </>
  )
  : t('reporting:noContract')
};

export default Overview
